import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { FileText, AlertCircle } from "lucide-react";

interface Client {
  id: number;
  name: string;
  email?: string;
  status?: string;
  phone?: string;
  last_contact?: string;
}

interface PaymentPlan {
  amount_agreed: number;
  amount_paid: number;
  amount_outstanding: number;
  first_payment_date: string;
}

interface ClientNote {
  id: string;
  note: string;
  created_at: string;
  is_response: boolean;
}

interface Document {
  id: string;
  name: string;
}

interface ClientSummaryProps {
  client: Client | null;
}

export const ClientSummary = ({ client }: ClientSummaryProps) => {
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [missingDocs, setMissingDocs] = useState<string[]>([]);

  useEffect(() => {
    if (!client?.id) return;

    const fetchData = async () => {
      // Fetch payment plan
      const { data: planData } = await supabase
        .from('payment_plans')
        .select('*')
        .eq('client_id', client.id)
        .maybeSingle();

      if (planData) setPaymentPlan(planData);

      // Fetch notes
      const { data: notesData } = await supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (notesData) setNotes(notesData);

      // Fetch documents to check what's missing
      const { data: docsData } = await supabase
        .from('documents')
        .select('name')
        .eq('client_id', client.id);

      const requiredDocs = [
        "ID Document",
        "Proof of Residence",
        "FICA",
        "Payslip",
        "Bank Statements (3 Months)"
      ];

      const uploadedDocs = docsData?.map(doc => doc.name.toLowerCase()) || [];
      const missing = requiredDocs.filter(doc => 
        !uploadedDocs.some(uploaded => 
          uploaded.toLowerCase().includes(doc.toLowerCase())
        )
      );
      
      setMissingDocs(missing);
    };

    fetchData();
  }, [client?.id]);

  if (!client) return null;

  return (
    <Card className="flex flex-col p-4 bg-[#1A1F2C] border-gray-700 h-[600px]">
      <div className="flex-1 overflow-y-auto space-y-4">
        <h2 className="text-lg font-semibold text-white">Client Summary</h2>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-400">Status</p>
            <p className="text-white">{client.status || 'Not set'}</p>
          </div>
          <div>
            <p className="text-gray-400">Last Contact</p>
            <p className="text-white">
              {client.last_contact 
                ? format(new Date(client.last_contact), 'dd MMM yyyy')
                : 'No recent contact'}
            </p>
          </div>
          <div>
            <p className="text-gray-400">Email</p>
            <p className="text-white">{client.email || 'No email'}</p>
          </div>
          <div>
            <p className="text-gray-400">Phone</p>
            <p className="text-white">{client.phone || 'No phone'}</p>
          </div>
        </div>

        {paymentPlan && (
          <div className="bg-gray-800/50 p-3 rounded-lg space-y-2">
            <p className="text-sm font-medium text-gray-400">Payment Plan</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-400">Monthly</p>
                <p className="text-white">R{paymentPlan.amount_agreed.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-400">Paid</p>
                <p className="text-green-400">R{paymentPlan.amount_paid.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-400">Outstanding</p>
                <p className="text-red-400">R{paymentPlan.amount_outstanding.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-400">First Payment</p>
                <p className="text-white">
                  {format(new Date(paymentPlan.first_payment_date), 'dd MMM yyyy')}
                </p>
              </div>
            </div>
          </div>
        )}

        {missingDocs.length > 0 && (
          <div className="bg-red-900/20 p-3 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm font-medium text-red-400">Outstanding Documents</p>
            </div>
            <div className="space-y-1">
              {missingDocs.map((doc, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-red-400" />
                  <p className="text-gray-300">{doc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-700 mt-4 pt-4">
        <p className="text-sm font-medium text-gray-400 mb-3">Recent Notes</p>
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {notes.length > 0 ? (
            notes.map((note) => (
              <div 
                key={note.id} 
                className={`p-2 rounded-lg text-sm ${
                  note.is_response ? 'bg-gray-800' : 'bg-gray-700'
                }`}
              >
                <p className="text-white">{note.note}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {format(new Date(note.created_at), 'dd MMM yyyy HH:mm')}
                </p>
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-sm">No notes yet</p>
          )}
        </div>
      </div>
    </Card>
  );
};